import { useMemo, useState } from 'react';
import { useAppSelector } from '../../../Redux/hooks';
import { selectSlotsByStudent } from '../../../Redux/selectors';
import { useTranslation } from '../../../i18n/useTranslation';
import { ApiError } from '../../../Models/ApiModel';
import { PremiumFeature } from '../../../Models/enums';
import type { LessonModel } from '../../../Models/LessonModel';
import { DocumentService } from '../../../Services/PublicService';
import { formatDate } from '../../../Utils/DateUtil';
import { isOwed } from '../../../Utils/LessonStatusUtil';
import { amountValue } from '../../../Utils/MoneyUtil';
import { toWhatsAppNumber, encodeMessage } from '../../../Utils/WhatsAppUtil';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { PremiumGate } from '../Premium/PremiumGate';
import './Reminders.css';
import { PremiumSubnav } from '../Premium/PremiumSubnav';
import { useDisplayName } from '../../../Utils/useDisplayName';

/**
 * Chasing payment, which is the job this feature exists for.
 *
 * A reminder is a WhatsApp message with a link to a payment request. The
 * request is created on the server first, so the message carries a real
 * document rather than a paragraph of numbers a parent has to trust.
 */
export function Reminders() {
  return (
    <PremiumGate feature={PremiumFeature.PaymentReminders}>
      <RemindersInner />
    </PremiumGate>
  );
}

interface DebtGroup {
  student: string;
  phone: string | null;
  lessons: LessonModel[];
  total: number;
}

function RemindersInner() {
  const displayName = useDisplayName();
  const { tr, language } = useTranslation();
  const students = useAppSelector(state => state.students.items);
  const lessons = useAppSelector(state => state.lessons.items);
  const slotsByStudent = useAppSelector(selectSlotsByStudent);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const groups = useMemo(() => {
    const now = new Date();
    const byStudent = new Map<string, DebtGroup>();
    for (const lesson of lessons) {
      if (!isOwed(lesson, slotsByStudent.get(lesson.student) ?? [], now)) continue;
      let group = byStudent.get(lesson.student);
      if (!group) {
        const student = students.find(candidate => candidate.name === lesson.student);
        // A contact marked as receiving payment messages wins over the
        // student's own number: the money conversation usually goes to a parent.
        const payer = student?.contacts?.find(contact => contact.receivesPayments && contact.phone);
        group = {
          student: lesson.student,
          phone: payer?.phone ?? student?.phone ?? null,
          lessons: [],
          total: 0
        };
        byStudent.set(lesson.student, group);
      }
      group.lessons.push(lesson);
      group.total += amountValue(lesson.amount);
    }
    return [...byStudent.values()].sort((a, b) => b.total - a.total);
  }, [lessons, students, slotsByStudent]);

  /**
   * The whole point of this function's shape: window.open is honoured only
   * from a direct click, and the link does not exist until the server has
   * answered. So the window is opened synchronously here and pointed at the
   * chat once the request has been created.
   */
  function sendReminder(group: DebtGroup) {
    const chat = window.open('', '_blank');
    setBusy(group.student);
    setError(null);

    DocumentService.createPaymentRequest(group.student, group.lessons.map(lesson => lesson.id))
      .then(created => {
        const number = toWhatsAppNumber(group.phone);
        const link = `${window.location.origin}/pay/${created.token}`;
        const lines = [
          tr('reminderGreeting', group.student),
          '',
          ...group.lessons.map(lesson =>
            `${formatDate(lesson.date, language)} · ${amountValue(lesson.amount)} ${tr('currency')}`),
          '',
          `${tr('total')}: ${created.total} ${tr('currency')}`,
          '',
          link
        ];
        // CRLF, not a bare newline: a message encoded with %0A alone loses
        // every line break on iOS and arrives as one paragraph.
        const message = encodeMessage(lines.join('\r\n'));
        const url = number
          ? `https://wa.me/${number}?text=${message}`
          : `https://wa.me/?text=${message}`;
        if (chat) chat.location.href = url;
        else window.location.href = url;
      })
      .catch(failure => {
        chat?.close();
        setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
      })
      .finally(() => setBusy(null));
  }

  if (error) return <ErrorState code={error} onRetry={() => setError(null)} />;

  return (
    <section className="reminders">
      <PremiumSubnav />
      <h1 className="screen-title">{tr('navReminders')}</h1>

      {groups.length === 0
        ? <EmptyState title={tr('noDebt')} hint={tr('noDebtHint')} />
        : (
          <ul className="reminder-rows">
            {groups.map(group => (
              <li key={group.student} className="reminder-row">
                <div className="reminder-row-main">
                  <span className="reminder-row-name">{displayName(group.student)}</span>
                  <span className="reminder-row-meta">
                    <bdi>{group.lessons.length}</bdi> · <MoneyText amount={group.total} />
                  </span>
                  {!group.phone && <span className="reminder-row-warning">{tr('noPhone')}</span>}
                </div>
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => sendReminder(group)}
                  disabled={busy === group.student}
                >
                  {tr('sendReminder')}
                </button>
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}
